import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import client from "@/lib/api";
import Ticker from "@/components/Ticker";
import Countdown from "@/components/Countdown";
import RaceWidget from "@/components/RaceWidget";
import AdBanner from "@/components/AdBanner";
import ContributeBox from "@/components/ContributeBox";
import MediaSocial from "@/components/MediaSocial";
import BonusPubblicita from "@/components/BonusPubblicita";
import PartnersSection from "@/components/PartnersSection";
import InstagramFeed from "@/components/InstagramFeed";
import NearbyEvents from "@/components/NearbyEvents";
import { HeroCard, ArticleCard } from "@/components/ArticleCards";
import { useLang, localizeArticle } from "@/context/LanguageContext";
import { Crown, ArrowRight, CalendarClock } from "lucide-react";
import { SPONSORS, logoCardCls } from "@/lib/constants";

export default function Home({ sponsors }) {
  const [params] = useSearchParams();
  const cat = params.get("cat");
  const { lang } = useLang();
  const [articles, setArticles] = useState([]);
  const [races, setRaces] = useState([]);

  useEffect(() => {
    client.get("/articles").then((r) => setArticles(r.data));
    client.get("/races").then((r) => setRaces(r.data));
  }, []);

  const filtered = cat ? articles.filter((a) => a.category === cat) : articles;
  const featured = filtered.find((a) => a.featured) || filtered[0];
  const FINNICO_SLUG = "storia-del-finnico-nascita-legend-magazine";
  const restRaw = filtered.filter((a) => a.slug !== featured?.slug);
  const finStory = restRaw.find((a) => a.slug === FINNICO_SLUG);
  const rest = finStory ? [finStory, ...restRaw.filter((a) => a.slug !== FINNICO_SLUG)] : restRaw;
  const secondary = rest.slice(0, 3);
  const grid = rest.slice(3);

  const world = articles.filter((a) => a.world);
  const worldMain = world.find((a) => a.world_featured) || world[0];
  const worldSide = world.filter((a) => a.slug !== worldMain?.slug).slice(0, 4);

  const banner1 = sponsors.find((s) => s.slot === "banner");
  const banner2 = sponsors.filter((s) => s.slot === "banner")[1];
  const inline = sponsors.filter((s) => s.slot === "inline");

  const finnicoRaw = articles.find((a) => a.slug === "storia-del-finnico-nascita-legend-magazine");
  const finnico = finnicoRaw ? localizeArticle(finnicoRaw, lang) : null;

  const tickerItems = articles.map((a) => ({ slug: a.slug, title: localizeArticle(a, lang).title }));

  return (
    <div>
      <Countdown />
      <Ticker items={tickerItems} />

      {/* Mensilità update notice */}
      <div className="bg-legend-yellow/20 border-b border-legend-yellow">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-center" data-testid="mensilita-notice">
          <CalendarClock size={16} className="text-legend-red shrink-0" />
          <p className="text-sm font-semibold text-legend-ink dark:text-legend-yellow">Nei prossimi giorni la mensilità verrà aggiornata con i nuovi contenuti.</p>
        </div>
      </div>

      {/* Hero band */}
      <div className="bg-gradient-to-b from-legend-ink to-slate-900 pt-8 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-6 w-1.5 bg-legend-red" />
            <h2 className="font-display font-black uppercase text-2xl text-white tracking-wide">
              {cat ? cat : "In Prima Pagina"}
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 animate-fade-up">
              <HeroCard article={featured} />
            </div>
            <div className="bg-white dark:bg-legend-paper rounded-sm p-2 divide-y divide-slate-100 dark:divide-slate-800">
              <div className="px-3 py-2 flex items-center gap-2">
                <span className="h-4 w-1 bg-legend-red" />
                <h3 className="font-display font-bold uppercase text-sm tracking-widest text-slate-500">Ultime notizie</h3>
              </div>
              {secondary.map((a) => (
                a.slug === FINNICO_SLUG ? (
                  <div key={a.slug} data-testid="pinned-finnico" className="relative border-l-4 border-legend-yellow bg-legend-yellow/5">
                    <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 bg-legend-yellow text-legend-ink text-[8px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm">★ La Storia</span>
                    <ArticleCard article={a} compact />
                  </div>
                ) : <ArticleCard key={a.slug} article={a} compact />
              ))}
            </div>
          </div>
        </div>
      </div>

      <RaceWidget races={races} />

      {!cat && worldMain && (
        <section className="bg-slate-100 dark:bg-legend-paper/40 border-y border-slate-200 dark:border-slate-800 py-10" data-testid="world-motorsport-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-6 w-1.5 bg-legend-red" />
              <h2 className="font-display font-black uppercase text-2xl tracking-wide">Dal Mondo Motorsport</h2>
            </div>
            <div className="grid lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <HeroCard article={worldMain} testid="world-hero-article" />
              </div>
              <div className="bg-white dark:bg-legend-paper rounded-sm p-2 divide-y divide-slate-100 dark:divide-slate-800" data-testid="world-latest">
                <div className="px-3 py-2 flex items-center gap-2">
                  <span className="h-4 w-1 bg-legend-red" />
                  <h3 className="font-display font-bold uppercase text-sm tracking-widest text-slate-500">Ultime dal mondo</h3>
                </div>
                {worldSide.map((a) => <ArticleCard key={a.slug} article={a} compact />)}
                {worldSide.length === 0 && <p className="px-3 py-4 text-sm text-slate-500">Altri articoli dal mondo motorsport in arrivo.</p>}
              </div>
            </div>
          </div>
        </section>
      )}

      <NearbyEvents races={races} />

      {/* Ad leaderboard */}
      <div className="max-w-7xl mx-auto px-4 mt-10">
        <AdBanner variant="advertise" shape="leaderboard" />
      </div>

      {/* Article grid + side ad */}
      <section className="max-w-7xl mx-auto px-4 mt-10">
        <div className="flex items-center gap-3 mb-5">
          <span className="h-6 w-1.5 bg-legend-red" />
          <h2 className="font-display font-black uppercase text-2xl tracking-wide">Dal Paddock</h2>
        </div>
        <div className="grid lg:grid-cols-[1fr_300px] gap-8">
          <div className="grid sm:grid-cols-2 gap-5">
            {finnico && (
              <Link
                to={`/articolo/${finnico.slug}`}
                data-testid="finnico-gold-feature"
                className="sm:col-span-2 group relative flex flex-col sm:flex-row rounded-sm overflow-hidden bg-white dark:bg-legend-paper border-[3px] border-legend-yellow ring-1 ring-legend-yellow/40 shadow-[0_8px_30px_rgba(212,175,55,0.25)] hover:shadow-[0_12px_40px_rgba(212,175,55,0.45)] hover:-translate-y-0.5 transition-all"
              >
                <div className="relative sm:w-2/5 aspect-[16/10] sm:aspect-auto overflow-hidden shrink-0">
                  <img src={finnico.cover_image} alt={finnico.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-legend-yellow text-legend-ink text-[10px] font-mono font-bold uppercase tracking-widest px-2.5 py-1 rounded-sm shadow">
                    <Crown size={12} /> La Storia
                  </span>
                </div>
                <div className="flex-1 p-5 sm:p-6 flex flex-col justify-center">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-legend-red mb-1">{finnico.category}</span>
                  <h3 className="font-display font-black uppercase text-2xl md:text-3xl leading-[0.95] text-slate-900 dark:text-white group-hover:text-legend-red transition-colors">{finnico.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 line-clamp-3">{finnico.excerpt}</p>
                  <span className="inline-flex items-center gap-1 mt-4 text-legend-ink dark:text-legend-yellow font-bold uppercase text-xs tracking-widest">
                    Leggi la storia <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            )}
            {grid.filter((a) => a.slug !== finnico?.slug).map((a) => <ArticleCard key={a.slug} article={a} />)}
            {grid.length === 0 && !finnico && <p className="text-slate-500 col-span-2">Altri articoli in arrivo con il prossimo numero.</p>}
          </div>
          <aside className="space-y-5">
            <AdBanner variant="sponsor" shape="half" sponsor={{ name: "MCG S.r.l. — Mulinari Costruzioni Generali", logo: "/sponsors/mcg-v2.png", url: "https://mulinaricostruzioni.it" }} />
            <div className="rounded-sm border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-legend-paper">
              <h4 className="font-display font-bold uppercase text-sm tracking-widest text-slate-500 mb-3">I nostri partner</h4>
              <div className="grid grid-cols-2 gap-3">
                {SPONSORS.map((s) => (
                  <a key={s.name} href={s.url} target="_blank" rel="noreferrer" title={s.name} data-testid={`sidebar-partner-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`flex items-center justify-center rounded-sm p-3 h-20 hover:border-legend-red hover:shadow-md transition-all ${logoCardCls(s.name)}`}>
                    <img src={s.logo} alt={s.name} className="max-h-full max-w-full object-contain" />
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <ContributeBox />

      {/* Subscription teaser */}
      <section className="max-w-7xl mx-auto px-4 my-14">
        <div className="rounded-sm bg-legend-red text-white p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <span className="inline-flex items-center gap-2 font-mono uppercase text-xs tracking-widest text-legend-yellow mb-2"><Crown size={14} /> Abbonati</span>
            <h2 className="font-display font-black uppercase text-3xl md:text-4xl leading-none">Accedi a tutti i contenuti esclusivi</h2>
            <p className="mt-2 text-white/90">Da 10€/anno. Articoli premium, archivio e tessera CONI con l'abbonamento Full.</p>
          </div>
          <Link to="/abbonamenti" data-testid="home-subscribe-cta" className="shrink-0 inline-flex items-center gap-2 bg-white text-legend-red font-bold uppercase tracking-wide px-6 py-3 rounded-sm hover:bg-legend-yellow hover:text-legend-ink transition-colors">
            Scopri gli abbonamenti <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <MediaSocial />
      <InstagramFeed />
      <PartnersSection sponsors={sponsors} />
      <BonusPubblicita />
    </div>
  );
}
